import { type ReactNode, useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { useAuth, logout } from '@/lib/auth'

// ─── Theme toggle ────────────────────────────────────────────────────────────

function ThemeToggle({ collapsed }: { collapsed: boolean }) {
  const [theme, setTheme] = useState<'pastel' | 'dim'>('pastel')

  useEffect(() => {
    const stored = localStorage.getItem('1klick-panel-theme')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const initial = stored === 'dim' || stored === 'pastel' ? stored : prefersDark ? 'dim' : 'pastel'
    setTheme(initial as 'pastel' | 'dim')
    document.documentElement.setAttribute('data-theme', initial)
  }, [])

  const selectTheme = (t: 'pastel' | 'dim') => {
    setTheme(t)
    document.documentElement.setAttribute('data-theme', t)
    localStorage.setItem('1klick-panel-theme', t)
  }

  const btnBase = 'flex-1 flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs transition-colors'
  const btnActive = 'bg-white/20 text-white font-medium'
  const btnInactive = 'text-gray-400 hover:bg-sidebar-hover hover:text-white'

  return (
    <div className={`flex ${collapsed ? 'flex-col' : ''} gap-1`}>
      <button
        onClick={() => selectTheme('pastel')}
        className={`${btnBase} ${theme === 'pastel' ? btnActive : btnInactive}`}
        title="Tema claro"
      >
        <span>☀</span>
        {!collapsed && <span>Claro</span>}
      </button>
      <button
        onClick={() => selectTheme('dim')}
        className={`${btnBase} ${theme === 'dim' ? btnActive : btnInactive}`}
        title="Tema oscuro"
      >
        <span>☾</span>
        {!collapsed && <span>Oscuro</span>}
      </button>
    </div>
  )
}

// ─── Nav link styles ──────────────────────────────────────────────────────────

function navLinkClass(isActive: boolean, collapsed: boolean): string {
  const base =
    'flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors'
  const active = 'bg-white/20 text-white font-medium'
  const inactive = 'text-gray-300 hover:bg-sidebar-hover hover:text-white'
  const collapsedClass = collapsed ? 'justify-center' : ''
  return `${base} ${isActive ? active : inactive} ${collapsedClass}`
}

// ─── Section label ────────────────────────────────────────────────────────────

function SectionLabel({ label, collapsed }: { label: string; collapsed: boolean }) {
  if (collapsed) return <div className="my-1 h-px bg-white/10" />
  return (
    <p className="mt-3 mb-1 px-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
      {label}
    </p>
  )
}

// ─── Shell ────────────────────────────────────────────────────────────────────

export function Shell({ children }: { children: ReactNode }) {
  const { sidebarCollapsed, toggleSidebar } = useAppStore()
  const { user } = useAuth()

  return (
    <div className="flex h-screen bg-base-200 text-base-content">
      {/* Sidebar */}
      <aside
        className={`flex flex-col bg-sidebar text-white transition-all duration-200 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4">
          {!sidebarCollapsed && (
            <span className="text-lg font-bold">1-Klick Sales</span>
          )}
          <button
            onClick={toggleSidebar}
            className="rounded p-1 text-gray-400 hover:bg-sidebar-hover hover:text-white"
          >
            {sidebarCollapsed ? '>' : '<'}
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-4 flex-1 overflow-y-auto px-2">
          {/* Main section */}
          <SectionLabel label="Principal" collapsed={sidebarCollapsed} />

          <NavLink
            to="/dashboard"
            className={({ isActive }) => navLinkClass(isActive, sidebarCollapsed)}
            title="Dashboard"
          >
            <span>Dashboard</span>
          </NavLink>

          <NavLink
            to="/clients"
            className={({ isActive }) => navLinkClass(isActive, sidebarCollapsed)}
            title="Clientes"
          >
            <span>Clientes</span>
          </NavLink>

          <NavLink
            to="/quotations"
            className={({ isActive }) => navLinkClass(isActive, sidebarCollapsed)}
            title="Cotizaciones"
          >
            <span>Cotizaciones</span>
          </NavLink>

          {/* Channel partners section */}
          <SectionLabel label="Canales" collapsed={sidebarCollapsed} />

          <NavLink
            to="/resellers"
            className={({ isActive }) => navLinkClass(isActive, sidebarCollapsed)}
            title="Resellers y Freelancers"
          >
            <span>Partners</span>
          </NavLink>

          <NavLink
            to="/commissions"
            className={({ isActive }) => navLinkClass(isActive, sidebarCollapsed)}
            title="Comisiones"
          >
            <span>Comisiones</span>
          </NavLink>

          {/* CMS section */}
          <SectionLabel label="CMS" collapsed={sidebarCollapsed} />

          <NavLink
            to="/cms/media"
            className={({ isActive }) => navLinkClass(isActive, sidebarCollapsed)}
            title="Media Library"
          >
            <span>Media</span>
          </NavLink>

          <NavLink
            to="/cms/globals"
            className={({ isActive }) => navLinkClass(isActive, sidebarCollapsed)}
            title="Contenido del sitio"
          >
            <span>Contenido</span>
          </NavLink>

          {/* System section */}
          <SectionLabel label="Sistema" collapsed={sidebarCollapsed} />

          <NavLink
            to="/users"
            className={({ isActive }) => navLinkClass(isActive, sidebarCollapsed)}
            title="Usuarios"
          >
            <span>Usuarios</span>
          </NavLink>

          <NavLink
            to="/config"
            className={({ isActive }) => navLinkClass(isActive, sidebarCollapsed)}
            title="Configuracion"
          >
            <span>Configuracion</span>
          </NavLink>
        </nav>

        {/* User info + Logout — pushed to bottom */}
        <div className="border-t border-white/10 px-3 py-3">
          {user && !sidebarCollapsed && (
            <div className="mb-2">
              <p className="truncate text-sm font-medium">{user.name}</p>
              <span className="inline-block rounded bg-white/20 px-1.5 py-0.5 text-xs">
                {user.role}
              </span>
            </div>
          )}
          {user && sidebarCollapsed && (
            <div className="mb-2 flex justify-center">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-xs font-medium"
                title={user.name}
              >
                {user.name?.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <ThemeToggle collapsed={sidebarCollapsed} />
          <button
            onClick={logout}
            className="flex w-full items-center justify-center gap-2 rounded px-2 py-1.5 text-sm text-gray-400 hover:bg-sidebar-hover hover:text-white"
            title="Cerrar sesion"
          >
            {sidebarCollapsed ? (
              <span className="text-base">X</span>
            ) : (
              'Cerrar sesion'
            )}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">{children}</div>
      </main>
    </div>
  )
}
